import React, { Fragment, useState, useEffect } from 'react';
import { changeRequestsApi } from '../api';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import { toast } from 'sonner';
import { validateDateFields, extractApiError } from '../utils/dateValidation';
import {
  RefreshCw,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileCheck,
  Play,
  Ban,
} from 'lucide-react';

const CHANGE_TYPES = [
  { value: 'document', label: 'Documento' },
  { value: 'process', label: 'Processo' },
  { value: 'product', label: 'Produto' },
  { value: 'system', label: 'Sistema' },
  { value: 'equipment', label: 'Equipamento' },
  { value: 'other', label: 'Outros' },
];

const PRIORITIES = [
  { value: 'low', label: 'Baixa', color: 'badge-gray' },
  { value: 'medium', label: 'Média', color: 'badge-blue' },
  { value: 'high', label: 'Alta', color: 'badge-yellow' },
  { value: 'critical', label: 'Crítica', color: 'badge-red' },
];

// Formats a date-only string (YYYY-MM-DD) as dd/mm/aaaa without timezone shift
const formatDateOnly = (dateStr) => {
  if (!dateStr) return '-';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

const RISK_FIELDS = [
  { key: 'regulatory_risk', detailsKey: 'regulatory_risk_details', label: 'Assuntos Regulatórios' },
  { key: 'management_risk', detailsKey: 'management_risk_details', label: 'Gerência' },
  { key: 'quality_risk', detailsKey: 'quality_risk_details', label: 'Qualidade' },
];

// Section block that mirrors a section of the F-032 "Controle de Mudança" paper form:
// a bold header bar with an italic instruction line underneath.
const FormSection = ({ title, instruction, children }) => (
  <div className="border border-gray-700 rounded-lg overflow-hidden">
    <div className="bg-blue-900/40 px-4 py-2 border-b border-gray-700">
      <p className="text-white font-bold text-xs uppercase tracking-wide">{title}</p>
      {instruction && <p className="text-blue-300 text-xs italic mt-0.5">{instruction}</p>}
    </div>
    <div className="p-4 bg-gray-800/30">{children}</div>
  </div>
);

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', color: 'badge-gray', icon: Clock },
  pending_analysis: { label: 'Aguardando Análise', color: 'badge-yellow', icon: Clock },
  in_analysis: { label: 'Em Análise', color: 'badge-blue', icon: RefreshCw },
  pending_approval: { label: 'Aguardando Aprovação', color: 'badge-yellow', icon: Clock },
  approved: { label: 'Aprovado', color: 'badge-green', icon: CheckCircle },
  rejected: { label: 'Rejeitado', color: 'badge-red', icon: XCircle },
  in_implementation: { label: 'Em Implementação', color: 'badge-blue', icon: Play },
  completed: { label: 'Concluído', color: 'badge-green', icon: FileCheck },
  cancelled: { label: 'Cancelado', color: 'badge-gray', icon: Ban },
};

const ChangeControl = () => {
  const [activeTab, setActiveTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    change_type: 'document',
    priority: 'medium',
    justification: '',
    affected_areas: '',
    affected_documents: '',
    event_date: '',
    target_date: '',
    department: '',
    contact_name: '',
    contact_phone: '',
    contact_mobile: '',
    contact_email: '',
    non_implementation_impact: '',
    alternatives: '',
    financial_impact: '',
    rollback_plan: '',
    regulatory_risk: null,
    regulatory_risk_details: '',
    management_risk: null,
    management_risk_details: '',
    quality_risk: null,
    quality_risk_details: '',
    action_plan: [],
    impact_analysis: '',
  });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadData();
  }, [filterStatus, filterPriority]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [requestsRes, statsRes] = await Promise.all([
        changeRequestsApi.list({ status: filterStatus || undefined, priority: filterPriority || undefined }),
        changeRequestsApi.stats(),
      ]);
      setRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (request = null) => {
    if (request) {
      try {
        const res = await changeRequestsApi.get(request.id);
        const fullRequest = res.data;
        setEditingRequest(fullRequest);
        setFormData({
          title: fullRequest.title,
          description: fullRequest.description,
          change_type: fullRequest.change_type,
          priority: fullRequest.priority,
          justification: fullRequest.justification || '',
          affected_areas: fullRequest.affected_areas || '',
          affected_documents: fullRequest.affected_documents || '',
          event_date: fullRequest.event_date || '',
          target_date: fullRequest.target_date || '',
          department: fullRequest.department || '',
          contact_name: fullRequest.contact_name || '',
          contact_phone: fullRequest.contact_phone || '',
          contact_mobile: fullRequest.contact_mobile || '',
          contact_email: fullRequest.contact_email || '',
          non_implementation_impact: fullRequest.non_implementation_impact || '',
          alternatives: fullRequest.alternatives || '',
          financial_impact: fullRequest.financial_impact || '',
          rollback_plan: fullRequest.rollback_plan || '',
          regulatory_risk: fullRequest.regulatory_risk ?? null,
          regulatory_risk_details: fullRequest.regulatory_risk_details || '',
          management_risk: fullRequest.management_risk ?? null,
          management_risk_details: fullRequest.management_risk_details || '',
          quality_risk: fullRequest.quality_risk ?? null,
          quality_risk_details: fullRequest.quality_risk_details || '',
          action_plan: fullRequest.action_plan || [],
          impact_analysis: fullRequest.impact_analysis || '',
        });
        setShowModal(true);
      } catch (error) {
        toast.error('Erro ao carregar dados da solicitação');
      }
    } else {
      setEditingRequest(null);
      setFormData({
        title: '',
        description: '',
        change_type: 'document',
        priority: 'medium',
        justification: '',
        affected_areas: '',
        affected_documents: '',
        event_date: '',
        target_date: '',
        department: '',
        contact_name: '',
        contact_phone: '',
        contact_mobile: '',
        contact_email: '',
        non_implementation_impact: '',
        alternatives: '',
        financial_impact: '',
        rollback_plan: '',
        regulatory_risk: null,
        regulatory_risk_details: '',
        management_risk: null,
        management_risk_details: '',
        quality_risk: null,
        quality_risk_details: '',
        action_plan: [],
        impact_analysis: '',
      });
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRequest(null);
  };

  const handleAddActionPlanRow = () => {
    setFormData({
      ...formData,
      action_plan: [...formData.action_plan, { action: '', responsible: '', deadline: '', cost: '', status: '' }],
    });
  };

  const handleActionPlanChange = (index, field, value) => {
    const updated = formData.action_plan.map((row, i) => (i === index ? { ...row, [field]: value } : row));
    setFormData({ ...formData, action_plan: updated });
  };

  const handleRemoveActionPlanRow = (index) => {
    setFormData({ ...formData, action_plan: formData.action_plan.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Preencha o Título da Solicitação');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Preencha o campo "Definições da Mudança"');
      return;
    }

    const dateError = validateDateFields([
      { value: formData.event_date, label: 'Data do Evento' },
      { value: formData.target_date, label: 'Data Alvo de Implementação' },
    ]);
    if (dateError) {
      toast.error(dateError);
      return;
    }
    
    setSaving(true);
    
    try {
      if (editingRequest) {
        await changeRequestsApi.update(editingRequest.id, formData);
        toast.success('Solicitação atualizada!');
      } else {
        await changeRequestsApi.create(formData);
        toast.success('Solicitação criada!');
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetails = async (request) => {
    try {
      const response = await changeRequestsApi.get(request.id);
      setSelectedRequest(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleUpdateStatus = async (requestId, newStatus, extraData = {}) => {
    try {
      await changeRequestsApi.update(requestId, { status: newStatus, ...extraData });
      toast.success('Status atualizado!');
      loadData();
      if (showDetailModal) {
        const response = await changeRequestsApi.get(requestId);
        setSelectedRequest(response.data);
      }
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleApprove = async (request) => {
    if (!window.confirm('Aprovar esta solicitação de mudança?')) return;
    try {
      await changeRequestsApi.approve(request.id);
      toast.success('Solicitação aprovada!');
      loadData();
    } catch (error) {
      toast.error('Erro ao aprovar');
    }
  };

  const handleReject = async (request) => {
    const reason = window.prompt('Motivo da rejeição:');
    if (!reason) return;
    try {
      await changeRequestsApi.reject(request.id, reason);
      toast.success('Solicitação rejeitada');
      loadData();
    } catch (error) {
      toast.error('Erro ao rejeitar');
    }
  };

  const handleDelete = async (request) => {
    if (!window.confirm('Cancelar esta solicitação?')) return;
    try {
      await changeRequestsApi.delete(request.id);
      toast.success('Solicitação cancelada');
      loadData();
    } catch (error) {
      toast.error('Erro ao cancelar');
    }
  };

  const filteredRequests = requests.filter(req =>
    req.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { id: 'requests', label: 'Solicitações', filter: null },
    { id: 'analysis', label: 'Em Análise', filter: 'in_analysis' },
    { id: 'approval', label: 'Aprovação', filter: 'pending_approval' },
    { id: 'implementation', label: 'Implementação', filter: 'in_implementation' },
  ];

  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (req) => <td className="font-medium text-white">{req.code}</td> },
    { key: 'title', label: 'Título', render: (req) => <td className="max-w-xs truncate">{req.title}</td> },
    { key: 'type', label: 'Tipo', render: (req) => <td>{CHANGE_TYPES.find(t => t.value === req.change_type)?.label}</td> },
    { key: 'priority', label: 'Prioridade', render: (req) => {
      const priorityConfig = PRIORITIES.find(p => p.value === req.priority) || PRIORITIES[1];
      return <td><span className={`badge ${priorityConfig.color}`}>{priorityConfig.label}</span></td>;
    } },
    { key: 'status', label: 'Status', render: (req) => {
      const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.draft;
      const StatusIcon = statusConfig.icon;
      return <td><span className={`badge ${statusConfig.color} flex items-center gap-1 w-fit`}><StatusIcon size={14} />{statusConfig.label}</span></td>;
    } },
    { key: 'date', label: 'Data', render: (req) => <td>{new Date(req.requested_date).toLocaleDateString('pt-BR')}</td> },
    { key: 'requester', label: 'Solicitante', render: (req) => <td>{req.requester_name}</td> },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (req) => (
      <td onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          {(req.status === 'pending_analysis' || req.status === 'pending_approval') && (
            <>
              <button
                onClick={() => handleApprove(req)}
                className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg"
                title="Aprovar"
              >
                <CheckCircle size={18} />
              </button>
              <button
                onClick={() => handleReject(req)}
                className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"
                title="Rejeitar"
              >
                <XCircle size={18} />
              </button>
            </>
          )}
          <button
            onClick={() => handleOpenModal(req)}
            className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg"
            title="Editar"
          >
            <Edit size={18} />
          </button>
          <button
            onClick={() => handleDelete(req)}
            className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"
            title="Cancelar"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'change-control', columns: COLUMNS });

  return (
    <div className="space-y-6" data-testid="change-control-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <RefreshCw className="text-green-500" />
            Controle de Mudanças
          </h1>
          <p className="text-gray-400 mt-1">Gerenciamento de solicitações de mudança</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary flex items-center gap-2"
          data-testid="add-change-btn"
        >
          <Plus size={20} />
          Nova Solicitação
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Total</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Pendentes</p>
            <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Em Andamento</p>
            <p className="text-2xl font-bold text-blue-400">{stats.in_progress}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Concluídas</p>
            <p className="text-2xl font-bold text-green-400">{stats.completed}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setFilterStatus(tab.filter || '');
            }}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'text-green-400 border-green-400'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar solicitações..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="input w-full sm:w-40"
        >
          <option value="">Prioridade</option>
          {PRIORITIES.map(p => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
        <div className="flex items-center">
          <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length} className="text-center py-8 text-gray-500">
                      Nenhuma solicitação encontrada
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map(req => (
                    <tr key={req.id} className="cursor-pointer hover:bg-gray-800/70" onClick={() => handleViewDetails(req)}>
                      {visibleColumns.map(col => (
                        <Fragment key={col.key}>{col.render(req)}</Fragment>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal — replica do formulário F-032 "Controle de Mudança" */}
      {showModal && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingRequest ? 'Editar Solicitação' : 'Nova Solicitação de Mudança'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Dados internos do sistema (não fazem parte do impresso F-032) */}
              <div className="border border-gray-700 rounded-lg p-4 space-y-4">
                <p className="text-gray-500 text-xs uppercase tracking-wide">Dados do sistema</p>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Título da Solicitação *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="input"
                    placeholder="Título da solicitação"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Tipo de Mudança *</label>
                    <select
                      value={formData.change_type}
                      onChange={(e) => setFormData({ ...formData, change_type: e.target.value })}
                      className="input"
                    >
                      {CHANGE_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Prioridade *</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className="input"
                    >
                      {PRIORITIES.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Áreas Afetadas</label>
                    <input
                      type="text"
                      value={formData.affected_areas}
                      onChange={(e) => setFormData({ ...formData, affected_areas: e.target.value })}
                      className="input"
                      placeholder="Ex: Produção, Qualidade"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Documentos Afetados</label>
                    <input
                      type="text"
                      value={formData.affected_documents}
                      onChange={(e) => setFormData({ ...formData, affected_documents: e.target.value })}
                      className="input"
                      placeholder="Ex: PQ-001, IT-005"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Data do Evento</label>
                    <input
                      type="date"
                      value={formData.event_date}
                      onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                      className="input"
                      min="1900-01-01"
                      max="2099-12-31"
                    />
                  </div>
                </div>
              </div>

              {/* Área/Departamento | Data da Mudança */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Área/Departamento</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="input"
                    placeholder="Ex: Qualidade"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Data da Mudança</label>
                  <input
                    type="date"
                    value={formData.target_date}
                    onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                    className="input"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
              </div>

              {/* Contato | Telefone | Celular | E-mail */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Contato</label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="input"
                    placeholder="Nome do contato"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Telefone</label>
                  <input
                    type="text"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Celular</label>
                  <input
                    type="text"
                    value={formData.contact_mobile}
                    onChange={(e) => setFormData({ ...formData, contact_mobile: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">E-mail</label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <FormSection title="Definições da Mudança *" instruction="Descrever o propósito da mudança">
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input min-h-[100px]"
                  placeholder="Descreva a mudança solicitada"
                />
              </FormSection>

              <FormSection title="Justificativa" instruction="Porque a proposta de mudança deve ser implementada?">
                <textarea
                  value={formData.justification}
                  onChange={(e) => setFormData({ ...formData, justification: e.target.value })}
                  className="input min-h-[80px]"
                />
              </FormSection>

              <FormSection title="Impacto de Não Implementar" instruction="Quais são os impactos caso a mudança não seja implementada?">
                <textarea
                  value={formData.non_implementation_impact}
                  onChange={(e) => setFormData({ ...formData, non_implementation_impact: e.target.value })}
                  className="input min-h-[80px]"
                />
              </FormSection>

              <FormSection title="Alternativas" instruction="De no mínimo 1 alternativa que poderia ser implementada ao invés de provocar a mudança.">
                <textarea
                  value={formData.alternatives}
                  onChange={(e) => setFormData({ ...formData, alternatives: e.target.value })}
                  className="input min-h-[80px]"
                />
              </FormSection>

              <FormSection title="Riscos" instruction="Especifique os riscos da implantação.">
                <div className="space-y-4">
                  {RISK_FIELDS.map((rf) => (
                    <div key={rf.key} className="space-y-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className="text-sm text-gray-200">► {rf.label}:</span>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-1 text-sm text-gray-300">
                            <input
                              type="radio"
                              name={rf.key}
                              checked={formData[rf.key] === true}
                              onChange={() => setFormData({ ...formData, [rf.key]: true })}
                            />
                            Sim
                          </label>
                          <label className="flex items-center gap-1 text-sm text-gray-300">
                            <input
                              type="radio"
                              name={rf.key}
                              checked={formData[rf.key] === false}
                              onChange={() => setFormData({ ...formData, [rf.key]: false, [rf.detailsKey]: '' })}
                            />
                            Não
                          </label>
                        </div>
                      </div>
                      {formData[rf.key] === true && (
                        <div>
                          <p className="text-xs text-gray-400 italic mb-1">Se sim relatar os riscos e plano de contingência:</p>
                          <textarea
                            value={formData[rf.detailsKey]}
                            onChange={(e) => setFormData({ ...formData, [rf.detailsKey]: e.target.value })}
                            className="input min-h-[60px]"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </FormSection>

              <FormSection title="Impacto Financeiro" instruction="Descrever qual o impacto financeiro desta implantação">
                <textarea
                  value={formData.financial_impact}
                  onChange={(e) => setFormData({ ...formData, financial_impact: e.target.value })}
                  className="input min-h-[80px]"
                />
              </FormSection>

              <FormSection title="Plano de Mudança" instruction="Definições de mudanças">
                <div className="space-y-2">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleAddActionPlanRow}
                      className="btn-secondary flex items-center gap-1 text-sm px-3 py-1.5"
                    >
                      <Plus size={16} /> Adicionar Ação
                    </button>
                  </div>
                  {formData.action_plan.length > 0 && (
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Ações</th>
                            <th>Responsável</th>
                            <th>Prazo</th>
                            <th>Custo</th>
                            <th>Status</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.action_plan.map((row, index) => (
                            <tr key={index}>
                              <td>
                                <input
                                  type="text"
                                  value={row.action}
                                  onChange={(e) => handleActionPlanChange(index, 'action', e.target.value)}
                                  className="input"
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={row.responsible}
                                  onChange={(e) => handleActionPlanChange(index, 'responsible', e.target.value)}
                                  className="input"
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={row.deadline}
                                  onChange={(e) => handleActionPlanChange(index, 'deadline', e.target.value)}
                                  className="input"
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={row.cost}
                                  onChange={(e) => handleActionPlanChange(index, 'cost', e.target.value)}
                                  className="input"
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={row.status}
                                  onChange={(e) => handleActionPlanChange(index, 'status', e.target.value)}
                                  className="input"
                                />
                              </td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveActionPlanRow(index)}
                                  className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"
                                  title="Remover"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </FormSection>

              <FormSection title="Plano de Volta" instruction="Especifique o plano de volta caso a mudança ocorra de forma inesperada.">
                <textarea
                  value={formData.rollback_plan}
                  onChange={(e) => setFormData({ ...formData, rollback_plan: e.target.value })}
                  className="input min-h-[80px]"
                />
              </FormSection>

              <FormSection
                title="Revisão da Requisição de Mudança (preenchido pelo gestor)"
                instruction="Copiar as aprovações e possíveis comentários do sistema neste campo"
              >
                <textarea
                  value={formData.impact_analysis}
                  onChange={(e) => setFormData({ ...formData, impact_analysis: e.target.value })}
                  className="input min-h-[70px]"
                />
              </FormSection>

              {editingRequest && (
                <FormSection title="Status Final da Requisição (preenchido pelo gestor)">
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Data final de aprovação</th>
                          <th>Nome</th>
                          <th>Título</th>
                          <th>Recomendação</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{editingRequest.approval_date ? new Date(editingRequest.approval_date).toLocaleDateString('pt-BR') : '-'}</td>
                          <td>{editingRequest.approver_name || '-'}</td>
                          <td>{editingRequest.approver_position || '-'}</td>
                          <td>
                            <label className="flex items-center gap-1 text-sm text-gray-300">
                              <input type="checkbox" checked={editingRequest.status === 'approved'} readOnly disabled /> Aprovado
                            </label>
                            <label className="flex items-center gap-1 text-sm text-gray-300">
                              <input type="checkbox" checked={editingRequest.status === 'rejected'} readOnly disabled /> Rejeitado
                            </label>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3">
                    <p className="text-xs text-gray-400 italic mb-1">
                      Instruções adicionais – Especifique qualquer informação adicional sobre a recomendação final
                    </p>
                    <p className="text-gray-300 text-sm bg-gray-900/40 rounded-lg px-3 py-2 min-h-[2.5rem]">
                      {editingRequest.approval_comments || editingRequest.rejection_reason || '-'}
                    </p>
                  </div>
                </FormSection>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary" disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                  {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRequest && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">{selectedRequest.code}</h2>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">{selectedRequest.title}</h3>
                <p className="text-gray-400">{selectedRequest.description}</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-gray-500 text-sm">Tipo</p>
                  <p className="text-white">{CHANGE_TYPES.find(t => t.value === selectedRequest.change_type)?.label}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Prioridade</p>
                  <span className={`badge ${PRIORITIES.find(p => p.value === selectedRequest.priority)?.color}`}>
                    {PRIORITIES.find(p => p.value === selectedRequest.priority)?.label}
                  </span>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Status</p>
                  <span className={`badge ${STATUS_CONFIG[selectedRequest.status]?.color}`}>
                    {STATUS_CONFIG[selectedRequest.status]?.label}
                  </span>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Data do Evento</p>
                  <p className="text-white">{formatDateOnly(selectedRequest.event_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Data Alvo</p>
                  <p className="text-white">{formatDateOnly(selectedRequest.target_date)}</p>
                </div>
              </div>

              {(selectedRequest.affected_areas || selectedRequest.affected_documents) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedRequest.affected_areas && (
                    <div>
                      <p className="text-gray-500 text-sm">Áreas Afetadas</p>
                      <p className="text-white">{selectedRequest.affected_areas}</p>
                    </div>
                  )}
                  {selectedRequest.affected_documents && (
                    <div>
                      <p className="text-gray-500 text-sm">Documentos Afetados</p>
                      <p className="text-white">{selectedRequest.affected_documents}</p>
                    </div>
                  )}
                </div>
              )}

              {(selectedRequest.department || selectedRequest.contact_name || selectedRequest.contact_phone || selectedRequest.contact_mobile || selectedRequest.contact_email) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {selectedRequest.department && (
                    <div>
                      <p className="text-gray-500 text-sm">Área/Departamento</p>
                      <p className="text-white">{selectedRequest.department}</p>
                    </div>
                  )}
                  {selectedRequest.contact_name && (
                    <div>
                      <p className="text-gray-500 text-sm">Contato</p>
                      <p className="text-white">{selectedRequest.contact_name}</p>
                    </div>
                  )}
                  {selectedRequest.contact_phone && (
                    <div>
                      <p className="text-gray-500 text-sm">Telefone</p>
                      <p className="text-white">{selectedRequest.contact_phone}</p>
                    </div>
                  )}
                  {selectedRequest.contact_mobile && (
                    <div>
                      <p className="text-gray-500 text-sm">Celular</p>
                      <p className="text-white">{selectedRequest.contact_mobile}</p>
                    </div>
                  )}
                  {selectedRequest.contact_email && (
                    <div>
                      <p className="text-gray-500 text-sm">E-mail</p>
                      <p className="text-white">{selectedRequest.contact_email}</p>
                    </div>
                  )}
                </div>
              )}

              {selectedRequest.justification && (
                <div>
                  <p className="text-gray-500 text-sm mb-1">Justificativa</p>
                  <p className="text-gray-300">{selectedRequest.justification}</p>
                </div>
              )}

              {selectedRequest.non_implementation_impact && (
                <div>
                  <p className="text-gray-500 text-sm mb-1">Impacto de Não Implementar</p>
                  <p className="text-gray-300">{selectedRequest.non_implementation_impact}</p>
                </div>
              )}

              {selectedRequest.alternatives && (
                <div>
                  <p className="text-gray-500 text-sm mb-1">Alternativas</p>
                  <p className="text-gray-300">{selectedRequest.alternatives}</p>
                </div>
              )}

              {selectedRequest.impact_analysis && (
                <div>
                  <p className="text-gray-500 text-sm mb-1">Análise de Impacto</p>
                  <p className="text-gray-300">{selectedRequest.impact_analysis}</p>
                </div>
              )}

              {RISK_FIELDS.some(rf => selectedRequest[rf.key] !== null && selectedRequest[rf.key] !== undefined) && (
                <div>
                  <p className="text-gray-500 text-sm mb-2">Riscos</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {RISK_FIELDS.map(rf => (
                      selectedRequest[rf.key] !== null && selectedRequest[rf.key] !== undefined && (
                        <div key={rf.key} className="bg-gray-700/30 rounded-lg p-3">
                          <p className="text-gray-300 text-sm font-medium">
                            {rf.label}: <span className={selectedRequest[rf.key] ? 'text-yellow-400' : 'text-green-400'}>
                              {selectedRequest[rf.key] ? 'Sim' : 'Não'}
                            </span>
                          </p>
                          {selectedRequest[rf.detailsKey] && (
                            <p className="text-gray-400 text-sm mt-1">{selectedRequest[rf.detailsKey]}</p>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {selectedRequest.financial_impact && (
                <div>
                  <p className="text-gray-500 text-sm mb-1">Impacto Financeiro</p>
                  <p className="text-gray-300">{selectedRequest.financial_impact}</p>
                </div>
              )}

              {selectedRequest.action_plan && selectedRequest.action_plan.length > 0 && (
                <div>
                  <p className="text-gray-500 text-sm mb-2">Plano de Mudança</p>
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Ações</th>
                          <th>Responsável</th>
                          <th>Prazo</th>
                          <th>Custo</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRequest.action_plan.map((row, index) => (
                          <tr key={index}>
                            <td>{row.action}</td>
                            <td>{row.responsible}</td>
                            <td>{row.deadline}</td>
                            <td>{row.cost}</td>
                            <td>{row.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selectedRequest.rollback_plan && (
                <div>
                  <p className="text-gray-500 text-sm mb-1">Plano de Volta</p>
                  <p className="text-gray-300">{selectedRequest.rollback_plan}</p>
                </div>
              )}

              {selectedRequest.impact_analysis && (
                <div>
                  <p className="text-gray-500 text-sm mb-1">Revisão da Requisição de Mudança</p>
                  <p className="text-gray-300">{selectedRequest.impact_analysis}</p>
                </div>
              )}

              {(selectedRequest.approval_comments || selectedRequest.rejection_reason) && (
                <div>
                  <p className="text-gray-500 text-sm mb-1">
                    {selectedRequest.status === 'rejected' ? 'Motivo da Rejeição' : 'Comentários da Aprovação'}
                  </p>
                  <p className="text-gray-300">{selectedRequest.rejection_reason || selectedRequest.approval_comments}</p>
                </div>
              )}

              {(selectedRequest.status === 'approved' || selectedRequest.status === 'rejected') && (
                <div>
                  <p className="text-gray-500 text-sm mb-2">Status Final da Requisição</p>
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Data final de aprovação</th>
                          <th>Nome</th>
                          <th>Título</th>
                          <th>Recomendação</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{selectedRequest.approval_date ? new Date(selectedRequest.approval_date).toLocaleDateString('pt-BR') : '-'}</td>
                          <td>{selectedRequest.approver_name || '-'}</td>
                          <td>{selectedRequest.approver_position || '-'}</td>
                          <td>{selectedRequest.status === 'approved' ? 'Aprovado' : 'Rejeitado'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-700">
                {selectedRequest.status === 'pending_analysis' && (
                  <button
                    onClick={() => handleUpdateStatus(selectedRequest.id, 'in_analysis')}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Play size={18} /> Iniciar Análise
                  </button>
                )}
                {selectedRequest.status === 'in_analysis' && (
                  <button
                    onClick={() => handleUpdateStatus(selectedRequest.id, 'pending_approval')}
                    className="btn-primary flex items-center gap-2"
                  >
                    <FileCheck size={18} /> Enviar para Aprovação
                  </button>
                )}
                {selectedRequest.status === 'approved' && (
                  <button
                    onClick={() => handleUpdateStatus(selectedRequest.id, 'in_implementation')}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Play size={18} /> Iniciar Implementação
                  </button>
                )}
                {selectedRequest.status === 'in_implementation' && (
                  <button
                    onClick={() => handleUpdateStatus(selectedRequest.id, 'completed')}
                    className="btn-primary flex items-center gap-2"
                  >
                    <CheckCircle size={18} /> Concluir
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChangeControl;
